/**
 * Sprint 23.8 — WorkspaceSettingsPreview (Settings tab).
 *
 * A lightweight read-only preview of workspace settings (name · mission · plan ·
 * members) + a "Full settings →" jump to `/settings/workspace`. Intentionally
 * minimal — the full editing surface lives at the route (risk-table note).
 */

import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Card } from "../primitives";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useWorkspaceOverview } from "../../hooks/use-workspace-overview";

function capitalize(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

export function WorkspaceSettingsPreview() {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const { data } = useWorkspaceOverview();

  return (
    <div style={{ padding: "12px 0" }}>
      <Card>
        <div style={rows}>
          <Row label="Name" value={data?.workspace?.name ?? "—"} />
          <Row label="Mission" value={data?.mission?.trim() || "Not set"} />
          <Row label="Plan" value={capitalize(data?.plan ?? "free")} />
          <Row label="Members" value={String(data?.members_count ?? 0)} />
          <Row label={capitalize(labels.unitPlural)} value={String(data?.agents_count ?? 0)} />
        </div>
        <button type="button" style={fullLink} onClick={() => navigate("/settings/workspace")}>
          Full settings →
        </button>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={row}>
      <span style={rowLabel}>{label}</span>
      <span style={rowValue}>{value}</span>
    </div>
  );
}

const rows: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  fontSize: 13,
};

const row: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
};

const rowLabel: CSSProperties = {
  color: "var(--color-text-tertiary)",
};

const rowValue: CSSProperties = {
  color: "var(--color-text-primary)",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const fullLink: CSSProperties = {
  marginTop: 12,
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
